import {
  Box,
  Button,
  Chip,
  Grid,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  List,
  ListItem,
  InputAdornment,
  TextField,
  Dialog,
  DialogContent,
  TablePagination,
  TableFooter,
} from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { CancelRounded, DeleteOutlineRounded, EditRounded } from '@material-ui/icons';
import { makeStyles, withStyles } from '@material-ui/styles';
import { listUserGroupPlatform, removeUserGroupByCode } from 'api/cms/user_group';
import UserGroupDialog from 'components/admin/provider_config/UserGroup';
import UserGroupPlatformDialog from 'components/admin/provider_config/UserGroupPlatform';
import Breadcrumbs from 'components/Breadcrumbs';
import ApplicationLayout from 'components/layouts/ApplicationLayout';
import TablePaginationActions from 'components/table/TablePaginationActions';
import useDebounce from 'hooks/useDebounce';
import { useState } from 'react';
import { Link, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import useSWR from 'swr';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGlow: 1,
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  gridLayout: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(1),
  },
  cellOverflowY: {
    height: 100,
    width: 'auto',
    overflowY: 'scroll',
  },
  cellOverflowX: {
    width: 'auto',
    overflowX: 'scroll',
  },
  flex: {
    display: 'flex',
  },
}));

const ColorButton = withStyles((theme) => ({
  root: {
    color: theme.palette.getContrastText(red[500]),
    backgroundColor: red[500],
    '&:hover': {
      backgroundColor: red[700],
    },
  },
}))(Button);

const UserGroupConfig = () => {
  let { providerCode } = useParams();
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openSeeMoreDialog, setOpenSeeMoreDialog] = useState(false);
  const [rowData, setRowData] = useState();
  const [rowPlatformData, setRowPlatformData] = useState();
  const [searchObj, setSearch] = useState();
  const searchDebounce = useDebounce(searchObj, 700);

  const { data, mutate } = useSWR(
    [page, rowsPerPage, searchDebounce],
    () =>
      listUserGroupPlatform(providerCode, {
        page: page + 1,
        limit: rowsPerPage,
        platform_code: searchDebounce?.platform_code,
        merchant_code: searchDebounce?.merchant_code,
      }),
    { revalidateOnFocus: false, refreshInterval: 0, shouldRetryOnError: false },
  );

  const onEdit = (key, row) => {
    // console.log(row);
    setRowData({ key, ...row });
    setOpenCreateDialog(true);
  };

  const onDeleteGroup = (key, row) => {
    Swal.fire({
      title: 'Delete Group',
      text: 'Are you sure to remove group',
      showLoaderOnConfirm: true,
      showCancelButton: true,
      preConfirm: async () => {
        try {
          const res = await removeUserGroupByCode(key);
          if (res.code === 0) {
            return res;
          } else {
            return Swal.showValidationMessage(`${res.data.code} ${res.data.message}`);
          }
        } catch (error) {
          Swal.showValidationMessage(`${error}`);
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({ title: 'Delete Successfully' });
        setRowData();
        mutate();
      }
    });
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    mutate();
    setRowData();
    setOpenCreateDialog(false);
  };

  const handleOpenSeeMoreDialog = (key, rows) => {
    setRowPlatformData({ key, ...rows });
    setOpenSeeMoreDialog(true);
  };

  const handleCloseSeeMoreDialog = () => {
    setRowData();
    setOpenSeeMoreDialog(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    if (event.target.value == -1) {
      event.target.value = parseInt(0, 10);
      setRowsPerPage(0);
    } else {
      setRowsPerPage(parseInt(event.target.value, 10));
    }
    setPage(0);
  };

  return (
    <ApplicationLayout>
      <Breadcrumbs />
      <Grid className={classes.gridLayout}>
        <Box bgcolor={'white'} className={classes.paper}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6" gutterBottom component="div">
              Group Config
            </Typography>
            <Button color="primary" variant="contained" onClick={() => handleOpenCreateDialog()}>
              Create Group
            </Button>
          </Box>
          <Box>
            <TextField
              size="small"
              variant="outlined"
              margin="normal"
              id="platform_code"
              label="Platform Code"
              onChange={(e) => {
                setSearch({
                  ...searchObj,
                  platform_code: e.target.value,
                });
              }}
              InputProps={{
                onClick: () => setSearch({ platform_code: '' }),
                endAdornment: (
                  <InputAdornment position="end">
                    <CancelRounded />
                  </InputAdornment>
                ),
              }}
              value={searchObj?.platform_code}
              defaultValue={searchObj?.platform_code}
              style={{ marginRight: '15px' }}
            />
            <TextField
              size="small"
              variant="outlined"
              margin="normal"
              id="merchant_code"
              label="Merchant Code"
              onChange={(e) => {
                setSearch({
                  ...searchObj,
                  merchant_code: e.target.value,
                });
              }}
              InputProps={{
                onClick: () => setSearch({ merchant_code: '' }),
                endAdornment: (
                  <InputAdornment position="end">
                    <CancelRounded />
                  </InputAdornment>
                ),
              }}
              value={searchObj?.merchant_code}
              defaultValue={searchObj?.merchant_code}
            />
          </Box>
        </Box>
        <TableContainer component={Paper}>
          <Table aria-label="simple table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Group Code</TableCell>
                <TableCell>Platforms/Merchants</TableCell>
                <TableCell>Configs</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                Object.keys(data.groups).map((key, idx) => (
                  <TableRow key={idx + 1}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{key}</TableCell>
                    <TableCell>
                      {data.groups[key].platforms.map((p, idx) => {
                        if (idx < 2) {
                          return (
                            <Chip
                              key={p.platform_code}
                              label={p.platform_name}
                              className={classes.chip}
                            />
                          );
                        } else {
                          return null;
                        }
                      })}
                      {data.groups[key].platforms.length > 1 ? (
                        <Button
                          size="small"
                          onClick={() => handleOpenSeeMoreDialog(key, data.groups[key])}
                        >
                          See More...
                        </Button>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Box className={classes.cellOverflowX}>
                        {JSON.stringify(data.groups[key].provider_configs)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gridGap={10}>
                        <Box>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            endIcon={<EditRounded />}
                            onClick={() => onEdit(key, data.groups[key])}
                          >
                            Edit
                          </Button>
                        </Box>
                        <Box>
                          <ColorButton
                            variant="contained"
                            size="small"
                            endIcon={<DeleteOutlineRounded />}
                            onClick={() => onDeleteGroup(key, data.groups[key])}
                          >
                            Delete Group
                          </ColorButton>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100, { label: 'All', value: -1 }]}
                  colSpan={5}
                  count={data?.total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <UserGroupDialog
          row={rowData}
          open={openCreateDialog}
          handleClose={handleCloseCreateDialog}
        />
        <UserGroupPlatformDialog
          row={rowPlatformData}
          open={openSeeMoreDialog}
          handleClose={handleCloseSeeMoreDialog}
        />
      </Grid>
    </ApplicationLayout>
  );
};

export default UserGroupConfig;
